import { InfoCircleOutlined } from '@ant-design/icons';
import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { confirm } from '@client/components/_ui/feedback/confirm-modal';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Progress } from '@client/components/_ui/feedback/Progress';
import { tokenValues } from '@client/design-system/__generated__';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createClientColumnHelper, PrismaTable, useClientTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { selectIsSuperAdmin, useAuthStore } from '@client/stores/AuthStore';
import type { RouterOutputs } from '@client/trpc/client';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import type {
    LargestRowData,
    TableColumnStats,
    TableIndexStats,
    TableOptimizationInsights,
    TableStorageAnalysis,
} from '@shared/types';
import { createFileRoute } from '@tanstack/react-router';
import {
    Alert,
    Card,
    Col,
    Drawer,
    Dropdown,
    Input,
    Row,
    Space,
    Statistic,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    Activity,
    BarChart2,
    BarChart3,
    Database,
    Download,
    HardDrive,
    Layers,
    Mail,
    RefreshCw,
    Users,
    Wrench,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

export const Route = createFileRoute('/admin-console/advanced/db-stats')({
    staticData: {
        ui: {
            title: 'Database statistics',
            menuDescription: 'View database statistics',
        },
        breadcrumb: {
            title: 'Database statistics',
        },
        permissions: ['admin_system:read'],
    },
    component: DatabaseStats,
});

type TableStatsRow = RouterOutputs['admin']['stats']['getDatabaseStats']['tables'][number];

type QueryParams = {
    searchTerm?: string;
};

function DatabaseStats() {
    const hasReadAccess = useAuthStore(
        (state) =>
            state.pendingOrgId === undefined &&
            state.user?.orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE &&
            state.org?.id === SYSTEM_ORG_IDS.SYSTEM_CONSOLE &&
            hasPermission(state.user?.roles ?? [], 'admin_system:read'),
    );

    if (!hasReadAccess) {
        return (
            <PageController route={Route}>
                <div className="mx-auto max-w-5xl p-6">
                    <Alert
                        type="warning"
                        showIcon
                        message="Admin system access required"
                        description="Database statistics are only available from the admin console to users with admin system access."
                    />
                </div>
            </PageController>
        );
    }

    return <DatabaseStatsContent />;
}

function DatabaseStatsContent() {
    const [selectedTable, setSelectedTable] = useState<{ schemaName: string; tableName: string } | null>(null);
    const canManage = useAuthStore((state) => hasPermission(state.user?.roles ?? [], 'admin_system:full'));
    const hasPrivilegedAccess = useAuthStore(selectIsSuperAdmin);
    const { formatBytes } = useFormatter();
    const { trpc } = useTrpc();

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {});

    const { data, error, isPending, refetch, isRefetching } = trpc.admin.stats.getDatabaseStats.useQuery({
        searchTerm: queryParams.searchTerm || undefined,
    });

    const { mutate: runAnalyze, isPending: isAnalyzing } = trpc.admin.stats.runDatabaseAnalyze.useMutation({
        onSuccess: (result) => {
            if (result.success) {
                message.success(result.message);
            } else {
                message.warning(result.message);
            }
            // Refresh the stats after analyze
            refetch();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const { mutate: runVacuum, isPending: isVacuuming } = trpc.admin.stats.runDatabaseVacuum.useMutation({
        onSuccess: (result) => {
            if (result.success) {
                message.success(result.message);
            } else {
                message.warning(result.message);
                if (result.errors && result.errors.length > 0) {
                    console.error('VACUUM errors:', result.errors);
                    modal.error({
                        title: 'VACUUM Errors',
                        content: (
                            <div>
                                <p>The following tables failed to vacuum:</p>
                                <ul className="list-disc pl-5">
                                    {result.errors.map((err, idx) => (
                                        <li key={idx}>
                                            <strong>{err.table}</strong>: {err.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ),
                    });
                }
            }
            // Refresh the stats after vacuum
            refetch();
        },
        onError: (error) => {
            if (error.message.includes('Privileged super admin access required')) {
                return;
            }
            errorMessage.show(error);
        },
    });

    const { mutate: runVacuumFull, isPending: isVacuumingFull } = trpc.admin.stats.runDatabaseVacuumFull.useMutation({
        onSuccess: (result) => {
            if (result.success) {
                message.success(result.message);
            } else {
                message.warning(result.message);
                if (result.errors && result.errors.length > 0) {
                    console.error('VACUUM FULL errors:', result.errors);
                    modal.error({
                        title: 'VACUUM FULL Errors',
                        content: (
                            <div>
                                <p>The following tables failed to vacuum:</p>
                                <ul className="list-disc pl-5">
                                    {result.errors.map((err, idx) => (
                                        <li key={idx}>
                                            <strong>{err.table}</strong>: {err.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ),
                    });
                }
            }
            // Refresh the stats after vacuum full
            refetch();
        },
        onError: (error) => {
            if (error.message.includes('Privileged super admin access required')) {
                return;
            }
            errorMessage.show(error);
        },
    });

    const columnHelper = createClientColumnHelper<TableStatsRow>();

    const columns = [
        columnHelper.accessor('tableName', {
            id: 'tableName',
            header: 'Table Name',
            sortable: 'tableName',
            cell: ({ row }) => (
                <Space>
                    <Typography.Text strong>{row.tableName}</Typography.Text>
                    {row.schemaName !== 'public' && (
                        <Tag variant="outlined" color="blue">
                            {row.schemaName}
                        </Tag>
                    )}
                </Space>
            ),
            hideable: false,
        }),
        columnHelper.accessor('rowCount', {
            id: 'rowCount',
            header: 'Row Count',
            sortable: 'rowCount',
            cell: ({ row }) => row.rowCount.toLocaleString(),
            align: 'right',
        }),
        columnHelper.accessor('totalSize', {
            id: 'totalSize',
            header: 'Total Size',
            sortable: 'totalSize',
            cell: ({ row }) => (
                <Tag variant="outlined" color="blue">
                    {formatBytes(row.totalSize)}
                </Tag>
            ),
            align: 'right',
        }),
        columnHelper.accessor('tableSize', {
            id: 'tableSize',
            header: 'Table Size',
            sortable: 'tableSize',
            cell: ({ row }) => formatBytes(row.tableSize),
            align: 'right',
        }),
        columnHelper.accessor('indexesSize', {
            id: 'indexesSize',
            header: 'Indexes Size',
            sortable: 'indexesSize',
            cell: ({ row }) => (
                <Tooltip title={`${row.indexCount} indexes (${row.unusedIndexCount} unused)`}>
                    <span>{formatBytes(row.indexesSize)}</span>
                </Tooltip>
            ),
            align: 'right',
        }),
        columnHelper.accessor('deadTuples', {
            id: 'deadTuples',
            header: (
                <Tooltip title="Dead tuples are old row versions that can be reclaimed by VACUUM">Dead Tuples</Tooltip>
            ),
            sortable: 'deadTuples',
            cell: ({ row }) => {
                const deadTuplePercentage = row.rowCount > 0 ? ((row.deadTuples / row.rowCount) * 100).toFixed(1) : '0';

                if (row.deadTuples > 10000) {
                    return (
                        <Tooltip
                            title={`${deadTuplePercentage}% of total rows. Consider running VACUUM to reclaim space.`}
                        >
                            <Tag variant="outlined" color="orange">
                                {row.deadTuples.toLocaleString()}
                            </Tag>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title={`${deadTuplePercentage}% of total rows`}>
                        <span>{row.deadTuples.toLocaleString()}</span>
                    </Tooltip>
                );
            },
            align: 'right',
        }),
        columnHelper.display({
            id: 'maintenance',
            header: 'Last Maintenance',
            cell: ({ row }) => {
                const lastVacuum = row.lastVacuum || row.lastAutoVacuum;
                const lastAnalyze = row.lastAnalyze || row.lastAutoAnalyze;

                return (
                    <Space direction="vertical" size="small">
                        {lastVacuum && (
                            <Tooltip title={row.lastAutoVacuum ? 'Auto Vacuum' : 'Manual Vacuum'}>
                                <Typography.Text type="secondary" className="text-xs">
                                    V: {DateTime.fromJSDate(new Date(lastVacuum)).toRelative()}
                                </Typography.Text>
                            </Tooltip>
                        )}
                        {lastAnalyze && (
                            <Tooltip title={row.lastAutoAnalyze ? 'Auto Analyze' : 'Manual Analyze'}>
                                <Typography.Text type="secondary" className="text-xs">
                                    A: {DateTime.fromJSDate(new Date(lastAnalyze)).toRelative()}
                                </Typography.Text>
                            </Tooltip>
                        )}
                        {!lastVacuum && !lastAnalyze && (
                            <Typography.Text type="secondary" className="text-xs">
                                Never
                            </Typography.Text>
                        )}
                    </Space>
                );
            },
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Space size="small">
                    <Button
                        size="small"
                        type="secondary"
                        icon={<BarChart2 className="h-3 w-3" />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAnalyzeTable(row);
                        }}
                    >
                        Analyze
                    </Button>
                    {hasPrivilegedAccess && (
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'vacuum',
                                        label: 'VACUUM',
                                        icon: <Wrench className="h-3 w-3" />,
                                        onClick: () => handleVacuum([`${row.schemaName}.${row.tableName}`]),
                                    },
                                    {
                                        key: 'vacuum-full',
                                        label: 'VACUUM FULL',
                                        icon: <Wrench className="h-3 w-3" />,
                                        danger: true,
                                        onClick: () => handleVacuumFull([`${row.schemaName}.${row.tableName}`]),
                                    },
                                ],
                            }}
                            placement="bottomRight"
                        >
                            <Button
                                size="small"
                                type="secondary"
                                icon={<Wrench className="h-3 w-3" />}
                                disabled={isVacuuming || isVacuumingFull}
                                onClick={(e) => e.stopPropagation()}
                            >
                                Vacuum
                            </Button>
                        </Dropdown>
                    )}
                </Space>
            ),
            width: hasPrivilegedAccess ? 200 : 100,
        }),
    ];

    const tableProps = useClientTable({
        data: data?.tables ?? [],
        columns,
        rowKey: 'tableName',
        loading: isPending,
        sorting: {
            defaultSort: { field: 'totalSize', direction: 'desc' },
        },
        pagination: {
            defaultPageSize: 20,
            showTotal: (total) => `Total ${total} tables`,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'db-stats-columns',
            enableReordering: true,
        },
    });

    const onSearch = (value: string) => {
        setQueryParams((prev) => ({ ...prev, searchTerm: value || undefined }));
    };

    const handleAnalyzeTable = (record: TableStatsRow) => {
        setSelectedTable({ schemaName: record.schemaName, tableName: record.tableName });
    };

    const {
        data: analysisData,
        isPending: isAnalysisLoading,
        error: analysisError,
    } = trpc.admin.stats.analyzeTable.useQuery(
        {
            schemaName: selectedTable?.schemaName || '',
            tableName: selectedTable?.tableName || '',
        },
        {
            enabled: !!selectedTable,
        },
    );

    const handleAnalyze = () => {
        confirm({
            title: 'Analyze Database Statistics',
            content: (
                <div>
                    <p>This will run PostgreSQL's ANALYZE command on all user tables to update table statistics.</p>
                    <p className="mt-2">Benefits:</p>
                    <ul className="ml-5 list-disc">
                        <li>Updates row count estimates</li>
                        <li>Improves query planner decisions</li>
                        <li>Ensures accurate statistics display</li>
                    </ul>
                    <p className="mt-2 text-status-warning">Note: This may take a few minutes for large databases.</p>
                </div>
            ),
            okText: 'Run Analyze',
            onOk: () => {
                message.info('Starting database analysis...');
                runAnalyze({});
            },
        });
    };

    const handleVacuum = (tableNames?: string[]) => {
        confirm({
            title: tableNames ? `VACUUM Table${tableNames.length > 1 ? 's' : ''}` : 'VACUUM Database',
            content: (
                <div>
                    <p>
                        This will run PostgreSQL's VACUUM command on{' '}
                        {tableNames ? 'the selected table(s)' : 'all user tables'} to reclaim storage occupied by dead
                        tuples.
                    </p>
                    <p className="mt-2">Benefits:</p>
                    <ul className="ml-5 list-disc">
                        <li>Reclaims storage from dead tuples</li>
                        <li>Updates visibility map</li>
                        <li>Prevents transaction ID wraparound</li>
                        <li>Does NOT lock tables (can run during normal operation)</li>
                    </ul>
                    <p className="mt-2 text-status-info">
                        Note: VACUUM runs in the background and may cause temporary performance impact on large tables.
                    </p>
                </div>
            ),
            okText: 'Run VACUUM',
            onOk: () => {
                message.info('Starting vacuum operation...');
                runVacuum({ tableNames });
            },
        });
    };

    const handleVacuumFull = (tableNames?: string[]) => {
        confirm({
            title: tableNames ? `VACUUM FULL Table${tableNames.length > 1 ? 's' : ''}` : 'VACUUM FULL Database',
            content: (
                <div>
                    <p className="font-semibold text-status-danger">
                        ⚠️ WARNING: VACUUM FULL requires an EXCLUSIVE lock on each table!
                    </p>
                    <p className="mt-2">
                        This will run PostgreSQL's VACUUM FULL command on{' '}
                        {tableNames ? 'the selected table(s)' : 'all user tables'} to completely rebuild the table and
                        reclaim all dead space.
                    </p>
                    <p className="mt-2">Effects:</p>
                    <ul className="ml-5 list-disc">
                        <li className="text-status-success">
                            Reclaims ALL dead space (more thorough than regular VACUUM)
                        </li>
                        <li className="text-status-success">Reduces table size on disk</li>
                        <li className="text-status-success">Rebuilds indexes</li>
                        <li className="text-status-danger">LOCKS the entire table - no reads or writes allowed</li>
                        <li className="text-status-danger">Can take a VERY long time on large tables</li>
                        <li className="text-status-danger">Requires extra disk space during operation</li>
                    </ul>
                    <p className="mt-2 font-semibold text-status-danger">
                        ⚠️ Only run during maintenance windows when the affected tables can be unavailable!
                    </p>
                </div>
            ),
            okText: 'Run VACUUM FULL',
            okDanger: true,
            onOk: () => {
                message.warning('Starting VACUUM FULL operation - tables will be locked!');
                runVacuumFull({ tableNames });
            },
        });
    };

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <div className="mb-6">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Database Size"
                                    value={data?.database.size || 0}
                                    formatter={(value) => formatBytes(Number(value))}
                                    prefix={<Database className="h-5 w-5" />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Total Tables"
                                    value={data?.database.tableCount || 0}
                                    prefix={<Layers className="h-5 w-5" />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Total Rows"
                                    value={data?.database.totalRows || 0}
                                    formatter={(value) => value.toLocaleString()}
                                    prefix={<HardDrive className="h-5 w-5" />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="Active Connections"
                                    value={data?.database.activeConnections || 0}
                                    suffix={`/ ${data?.database.totalConnections || 0}`}
                                    prefix={<Users className="h-5 w-5" />}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="mt-4">
                        <Col xs={24} md={12}>
                            <Card>
                                <Statistic
                                    title="Cache Hit Ratio"
                                    value={data?.database.cacheHitRatio || 0}
                                    suffix="%"
                                    styles={{
                                        content: {
                                            color:
                                                (data?.database.cacheHitRatio || 0) > 90
                                                    ? 'var(--color-status-success)'
                                                    : 'var(--color-status-danger)',
                                        },
                                    }}
                                    prefix={<Activity className="h-5 w-5" />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <EmailClassificationStorageCard />
                        </Col>
                    </Row>
                </div>

                <TableToolbar
                    filters={
                        <Input.Search
                            placeholder="Search tables"
                            allowClear
                            enterButton="Search"
                            defaultValue={queryParams.searchTerm}
                            onSearch={onSearch}
                            className="w-96"
                        />
                    }
                    actions={
                        <Space>
                            {hasPrivilegedAccess && (
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'vacuum',
                                                label: 'VACUUM Database',
                                                icon: <Wrench className="h-4 w-4" />,
                                                onClick: () => handleVacuum(),
                                            },
                                            {
                                                key: 'vacuum-full',
                                                label: 'VACUUM FULL Database',
                                                icon: <Wrench className="h-4 w-4" />,
                                                danger: true,
                                                onClick: () => handleVacuumFull(),
                                            },
                                        ],
                                    }}
                                    placement="bottomRight"
                                >
                                    <Button
                                        icon={<Wrench className="h-4 w-4" />}
                                        loading={isVacuuming || isVacuumingFull}
                                        disabled={isPending || isRefetching}
                                    >
                                        Maintenance
                                    </Button>
                                </Dropdown>
                            )}
                            {canManage && (
                                <Button
                                    icon={<BarChart3 className="h-4 w-4" />}
                                    onClick={handleAnalyze}
                                    loading={isAnalyzing}
                                    disabled={isPending || isRefetching || isVacuuming || isVacuumingFull}
                                >
                                    Analyze Database
                                </Button>
                            )}
                            <Button
                                icon={<RefreshCw className="h-4 w-4" />}
                                onClick={() => refetch()}
                                loading={isRefetching}
                                disabled={isAnalyzing || isVacuuming || isVacuumingFull}
                            >
                                Refresh
                            </Button>
                        </Space>
                    }
                />

                <PrismaTable
                    {...tableProps}
                    onRow={({ tableName, schemaName }) => ({
                        className: 'cursor-pointer',
                        onClick() {
                            setSelectedTable({ schemaName, tableName });
                        },
                    })}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>
                                    <strong>Total</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <strong>{data?.database.totalRows.toLocaleString()}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                    <Tag variant="outlined" color="blue">
                                        <strong>{formatBytes(data?.database.size || 0)}</strong>
                                    </Tag>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} colSpan={5} />
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                <Drawer
                    title={`Table Analysis: ${selectedTable?.schemaName}.${selectedTable?.tableName}`}
                    styles={{ wrapper: { width: '90vw' } }}
                    open={!!selectedTable}
                    onClose={() => setSelectedTable(null)}
                >
                    {isAnalysisLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Space direction="vertical" align="center">
                                <RefreshCw className="h-8 w-8 animate-spin text-subtle-foreground" />
                                <Typography.Text type="secondary">Analyzing table...</Typography.Text>
                            </Space>
                        </div>
                    ) : analysisError ? (
                        <div className="flex items-center justify-center py-20">
                            <Space direction="vertical" align="center">
                                <Typography.Text type="danger">Error analyzing table</Typography.Text>
                                <Typography.Text type="secondary">{analysisError.message}</Typography.Text>
                            </Space>
                        </div>
                    ) : analysisData ? (
                        <Tabs
                            items={[
                                {
                                    key: 'columns',
                                    label: 'Columns',
                                    children: <ColumnAnalysis data={analysisData.columns} />,
                                },
                                {
                                    key: 'storage',
                                    label: 'Storage',
                                    children: <StorageAnalysis data={analysisData.storage} />,
                                },
                                {
                                    key: 'indexes',
                                    label: 'Indexes',
                                    children: <IndexAnalysis data={analysisData.indexes} />,
                                },
                                {
                                    key: 'largestRows',
                                    label: 'Largest Rows',
                                    children: <LargestRowsAnalysis data={analysisData.largestRows} />,
                                },
                                {
                                    key: 'insights',
                                    label: 'Insights',
                                    children: <InsightsPanel data={analysisData.insights} />,
                                },
                            ]}
                        />
                    ) : (
                        <div className="flex items-center justify-center py-20">
                            <Typography.Text type="secondary">No data available</Typography.Text>
                        </div>
                    )}
                </Drawer>
            </TRPCErrorBoundary>
        </PageController>
    );
}

// Email Classification Storage Card Component
function EmailClassificationStorageCard() {
    const { trpcClient } = useTrpc();
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const stats = await trpcClient.admin.stats.getMessageClassificationStorageStats.query();
            const dataStr = JSON.stringify(stats, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `message-classification-storage-stats-${DateTime.now().toFormat('yyyy-MM-dd-HHmmss')}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('Message classification storage stats exported');
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Card>
            <div className="flex items-center justify-between">
                <Space direction="vertical" size="small">
                    <Space className="text-muted-foreground">
                        <Mail className="h-5 w-5" />
                        <Typography.Text type="secondary">Email classification storage</Typography.Text>
                    </Space>
                    <Typography.Text className="text-muted-foreground text-sm">
                        Download storage statistics for the EmailClassification table
                    </Typography.Text>
                </Space>
                <Button icon={<Download className="h-4 w-4" />} onClick={handleDownload} loading={isDownloading}>
                    Download JSON
                </Button>
            </div>
        </Card>
    );
}

// Column Analysis Component
function ColumnAnalysis({ data }: { data: TableColumnStats[] }) {
    const { formatBytes, formatPercentage } = useFormatter();

    const columnHelper = createClientColumnHelper<TableColumnStats>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('columnName', {
                id: 'columnName',
                header: 'Column Name',
                cell: ({ row }) => <Typography.Text strong>{row.columnName}</Typography.Text>,
                fixed: 'left',
                hideable: false,
            }),
            columnHelper.accessor('dataType', {
                id: 'dataType',
                header: 'Data Type',
                cell: ({ row }) => <Tag variant="outlined">{row.dataType}</Tag>,
            }),
            columnHelper.accessor('diskSpaceBytes', {
                id: 'diskSpace',
                header: 'Disk Space',
                sortable: 'diskSpaceBytes',
                cell: ({ row }) => (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Typography.Text>{formatBytes(row.diskSpaceBytes)}</Typography.Text>
                        <Progress
                            percent={row.diskSpacePercentage}
                            size="small"
                            format={(percent) => `${percent?.toFixed(2)}%`}
                        />
                    </Space>
                ),
            }),
            columnHelper.accessor('nullFraction', {
                id: 'nullFraction',
                header: 'Null %',
                cell: ({ row }) => formatPercentage(row.nullFraction),
                align: 'right',
            }),
            columnHelper.accessor('distinctValues', {
                id: 'distinctValues',
                header: 'Distinct Values',
                cell: ({ row }) => (row.distinctValues === -1 ? 'All unique' : row.distinctValues.toLocaleString()),
                align: 'right',
            }),
            columnHelper.accessor('averageWidth', {
                id: 'averageWidth',
                header: 'Avg Width',
                cell: ({ row }) => `${row.averageWidth} bytes`,
                align: 'right',
            }),
        ],
        [columnHelper, formatBytes, formatPercentage],
    );

    const tableProps = useClientTable({
        data,
        columns,
        rowKey: 'columnName',
        pagination: false,
        sorting: { defaultSort: { field: 'diskSpaceBytes', direction: 'desc' } },
    });

    return <PrismaTable {...tableProps} />;
}

// Storage Analysis Component
function StorageAnalysis({ data }: { data: TableStorageAnalysis }) {
    const { formatBytes } = useFormatter();

    return (
        <div>
            <Row gutter={[16, 16]} className="mb-6">
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Size"
                            value={data.totalTableSize}
                            formatter={(value) => formatBytes(Number(value))}
                            prefix={<Database className="h-4 w-4" />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Main Table"
                            value={data.mainTableSize}
                            formatter={(value) => formatBytes(Number(value))}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="TOAST"
                            value={data.toastTableSize}
                            formatter={(value) => formatBytes(Number(value))}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Indexes"
                            value={data.indexesSize}
                            formatter={(value) => formatBytes(Number(value))}
                        />
                    </Card>
                </Col>
            </Row>

            {data.storageBreakdown && (
                <Card title="Storage Breakdown" className="mb-4">
                    <Typography.Paragraph type="secondary" className="mb-4">
                        <InfoCircleOutlined className="mr-1" />
                        Column sizes are estimates based on average width and row count. PostgreSQL adds significant
                        overhead for row headers, page structure, and alignment.
                    </Typography.Paragraph>

                    <Row gutter={[16, 16]} className="mb-4">
                        <Col span={12}>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span>Estimated Data Size:</span>
                                    <Tag variant="outlined" color="blue">
                                        {formatBytes(data.storageBreakdown.estimatedDataSize)}
                                    </Tag>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Row Headers (23 bytes/row):</span>
                                    <Tag variant="outlined">{formatBytes(data.storageBreakdown.rowHeaderOverhead)}</Tag>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Page Headers:</span>
                                    <Tag variant="outlined">
                                        {formatBytes(data.storageBreakdown.pageHeaderOverhead)}
                                    </Tag>
                                </div>
                                <div className="flex items-center justify-between">
                                    <Tooltip title="Includes dead tuples, fill factor, alignment padding, and other PostgreSQL internals">
                                        <span>
                                            Unaccounted Space <InfoCircleOutlined />:
                                        </span>
                                    </Tooltip>
                                    <Tag variant="outlined" color="orange">
                                        {formatBytes(data.storageBreakdown.unaccountedSpace)}
                                    </Tag>
                                </div>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="space-y-2">
                                <Typography.Text strong>Percentage Breakdown</Typography.Text>
                                <Progress
                                    percent={100}
                                    success={{ percent: data.storageBreakdown.percentageBreakdown.data }}
                                    format={() => (
                                        <span className="text-xs">
                                            Data: {data.storageBreakdown?.percentageBreakdown.data.toFixed(1)}%
                                        </span>
                                    )}
                                />
                                <Progress
                                    percent={data.storageBreakdown.percentageBreakdown.rowHeaders}
                                    strokeColor={tokenValues.color.antd.open}
                                    format={(percent) => `Row Headers: ${percent?.toFixed(1)}%`}
                                />
                                <Progress
                                    percent={data.storageBreakdown.percentageBreakdown.pageHeaders}
                                    strokeColor={tokenValues.color.info[500]}
                                    format={(percent) => `Page Headers: ${percent?.toFixed(1)}%`}
                                />
                                <Progress
                                    percent={data.storageBreakdown.percentageBreakdown.unaccounted}
                                    strokeColor={tokenValues.color.antd.reopened}
                                    format={(percent) => `Unaccounted: ${percent?.toFixed(1)}%`}
                                />
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}

            {data.diskSpaceByColumn.length > 0 && (
                <Card
                    title={
                        <span>
                            Column Disk Usage
                            <Tooltip title="These are estimates based on average column width × row count. Actual disk usage includes PostgreSQL overhead.">
                                <InfoCircleOutlined className="ml-2 text-subtle-foreground" />
                            </Tooltip>
                        </span>
                    }
                >
                    <DiskSpaceByColumnTable data={data.diskSpaceByColumn} formatBytes={formatBytes} />
                </Card>
            )}
        </div>
    );
}

// Type for disk space by column data
type DiskSpaceByColumn = { column: string; bytes: number };

// Disk Space by Column Table Component
function DiskSpaceByColumnTable({
    data,
    formatBytes,
}: {
    data: DiskSpaceByColumn[];
    formatBytes: (bytes: number) => string;
}) {
    const columnHelper = createClientColumnHelper<DiskSpaceByColumn>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('column', {
                id: 'column',
                header: 'Column',
            }),
            columnHelper.accessor('bytes', {
                id: 'bytes',
                header: 'Estimated Size',
                sortable: 'bytes',
                cell: ({ row }) => formatBytes(row.bytes),
                align: 'right',
            }),
        ],
        [columnHelper, formatBytes],
    );

    const tableProps = useClientTable({
        data,
        columns,
        rowKey: 'column',
        pagination: false,
        sorting: { defaultSort: { field: 'bytes', direction: 'desc' } },
    });

    const totalBytes = useMemo(() => data.reduce((sum, col) => sum + col.bytes, 0), [data]);

    return (
        <PrismaTable
            {...tableProps}
            summary={() => (
                <Table.Summary fixed>
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                            <strong>Total Estimated</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                            <strong>{formatBytes(totalBytes)}</strong>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                </Table.Summary>
            )}
        />
    );
}

// Index Analysis Component
function IndexAnalysis({ data }: { data: TableIndexStats[] }) {
    const { formatBytes } = useFormatter();

    const columnHelper = createClientColumnHelper<TableIndexStats>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('indexName', {
                id: 'indexName',
                header: 'Index Name',
                cell: ({ row }) => (
                    <Space>
                        <Typography.Text strong>{row.indexName}</Typography.Text>
                        {row.isUnused && (
                            <Tag variant="outlined" color="orange">
                                Unused
                            </Tag>
                        )}
                    </Space>
                ),
            }),
            columnHelper.accessor('size', {
                id: 'size',
                header: 'Size',
                sortable: 'size',
                cell: ({ row }) => formatBytes(row.size),
                align: 'right',
            }),
            columnHelper.accessor('scansCount', {
                id: 'scansCount',
                header: 'Scan Count',
                sortable: 'scansCount',
                cell: ({ row }) => row.scansCount.toLocaleString(),
                align: 'right',
            }),
            columnHelper.accessor('columns', {
                id: 'columns',
                header: 'Columns',
                cell: ({ row }) => <Tag variant="outlined">{row.columns.join(', ')}</Tag>,
            }),
        ],
        [columnHelper, formatBytes],
    );

    const tableProps = useClientTable({
        data,
        columns,
        rowKey: 'indexName',
        pagination: false,
        sorting: { defaultSort: { field: 'size', direction: 'desc' } },
    });

    return <PrismaTable {...tableProps} />;
}

// Row Data Preview Component - shows first 3 columns of row data
function RowDataPreview({ rowData }: { rowData: Record<string, unknown> }) {
    const entries = Object.entries(rowData);
    const previewEntries = entries.slice(0, 3);
    const hasMore = entries.length > 3;

    return (
        <div className="space-y-1">
            {previewEntries.map(([key, value]) => (
                <div key={key} className="flex items-start gap-2">
                    <Typography.Text type="secondary" className="min-w-[100px] text-xs">
                        {key}:
                    </Typography.Text>
                    <Typography.Text className="break-all text-xs">
                        {value === null ? (
                            <Tag variant="outlined" color="default" className="text-xs">
                                NULL
                            </Tag>
                        ) : typeof value === 'object' ? (
                            <Tag variant="outlined" color="purple" className="text-xs">
                                {JSON.stringify(value).substring(0, 50)}...
                            </Tag>
                        ) : (
                            String(value).substring(0, 100)
                        )}
                    </Typography.Text>
                </div>
            ))}
            {hasMore && (
                <Typography.Text type="secondary" className="text-xs italic">
                    ...and {entries.length - 3} more columns
                </Typography.Text>
            )}
        </div>
    );
}

// Expanded Row Content Component - shows full JSON data
function ExpandedRowContent({ record }: { record: LargestRowData }) {
    return (
        <Card size="small" className="ml-8">
            <Typography.Title level={5} className="mb-2">
                Complete Row Data
            </Typography.Title>
            <pre className="overflow-auto rounded bg-muted p-3 text-xs">{JSON.stringify(record.rowData, null, 2)}</pre>
        </Card>
    );
}

// Largest Rows Analysis Component
function LargestRowsAnalysis({ data }: { data?: LargestRowData[] }) {
    const { formatBytes, formatPercentage } = useFormatter();

    const columnHelper = createClientColumnHelper<LargestRowData>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('rowIdentifier', {
                id: 'rowIdentifier',
                header: 'Row ID',
                cell: ({ row }) => (
                    <Tooltip title="Physical row location (ctid)">
                        <Typography.Text code className="text-xs">
                            {row.rowIdentifier}
                        </Typography.Text>
                    </Tooltip>
                ),
                width: 120,
            }),
            columnHelper.accessor('rowSize', {
                id: 'rowSize',
                header: 'Size',
                sortable: 'rowSize',
                cell: ({ row }) => (
                    <Space direction="vertical" size="small">
                        <Tag variant="outlined" color="blue" className="font-mono">
                            {formatBytes(row.rowSize)}
                        </Tag>
                        {row.sizePercentage && row.sizePercentage > 0 && (
                            <Typography.Text type="secondary" className="text-xs">
                                {formatPercentage(row.sizePercentage / 100)} of table
                            </Typography.Text>
                        )}
                    </Space>
                ),
                width: 150,
            }),
            columnHelper.display({
                id: 'rowData',
                header: 'Row Data Preview',
                cell: ({ row }) => <RowDataPreview rowData={row.rowData} />,
            }),
        ],
        [columnHelper, formatBytes, formatPercentage],
    );

    const tableProps = useClientTable({
        data: data ?? [],
        columns,
        rowKey: 'rowIdentifier',
        pagination: false,
        sorting: { defaultSort: { field: 'rowSize', direction: 'desc' } },
    });

    // Calculate statistics
    const stats = useMemo(() => {
        if (!data || data.length === 0) return null;
        const totalSize = data.reduce((sum, row) => sum + row.rowSize, 0);
        return {
            largestRow: data[0].rowSize,
            averageSize: totalSize / data.length,
            totalSize,
        };
    }, [data]);

    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center py-20">
                <Space direction="vertical" align="center">
                    <Database className="h-12 w-12 text-subtle-foreground" />
                    <Typography.Text type="secondary">No row size data available</Typography.Text>
                </Space>
            </div>
        );
    }

    return (
        <div>
            <Card className="mb-4">
                <Typography.Paragraph>
                    <InfoCircleOutlined className="mr-1" />
                    Showing the 20 largest rows by actual disk space usage. Sizes include all PostgreSQL overhead (row
                    headers, alignment padding, TOAST pointers, etc.)
                </Typography.Paragraph>
                <Typography.Paragraph type="secondary" className="mb-0">
                    Row sizes are calculated using PostgreSQL's <Typography.Text code>pg_column_size()</Typography.Text>{' '}
                    function, which returns the compressed size as stored on disk.
                </Typography.Paragraph>
            </Card>

            <PrismaTable
                {...tableProps}
                expandable={{
                    expandedRowRender: (record) => <ExpandedRowContent record={record} />,
                    rowExpandable: () => true,
                }}
            />

            {stats && (
                <Card className="mt-4" size="small">
                    <Row gutter={16}>
                        <Col span={8}>
                            <Statistic
                                title="Largest Row"
                                value={stats.largestRow}
                                formatter={(value) => formatBytes(Number(value))}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Average Size (Top 20)"
                                value={stats.averageSize}
                                formatter={(value) => formatBytes(Number(value))}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Total Size (Top 20)"
                                value={stats.totalSize}
                                formatter={(value) => formatBytes(Number(value))}
                            />
                        </Col>
                    </Row>
                </Card>
            )}
        </div>
    );
}

// Insights Panel Component
function InsightsPanel({ data }: { data: TableOptimizationInsights }) {
    return (
        <div className="space-y-4">
            {data.recommendations.length > 0 && (
                <Card title="Recommendations" className="border-orange-200">
                    <ul className="list-disc space-y-2 pl-5">
                        {data.recommendations.map((rec, index) => (
                            <li key={index}>
                                <Typography.Text>{rec}</Typography.Text>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {data.largestColumns.length > 0 && (
                <Card title="Largest Columns">
                    <Space wrap>
                        {data.largestColumns.map((col) => (
                            <Tag variant="outlined" key={col} color="blue">
                                {col}
                            </Tag>
                        ))}
                    </Space>
                </Card>
            )}

            {data.highNullColumns.length > 0 && (
                <Card title="High Null Columns">
                    <Space wrap>
                        {data.highNullColumns.map((col) => (
                            <Tag variant="outlined" key={col} color="orange">
                                {col}
                            </Tag>
                        ))}
                    </Space>
                </Card>
            )}

            {data.unusedIndexes.length > 0 && (
                <Card title="Unused Indexes">
                    <Space wrap>
                        {data.unusedIndexes.map((idx) => (
                            <Tag variant="outlined" key={idx} color="red">
                                {idx}
                            </Tag>
                        ))}
                    </Space>
                </Card>
            )}
        </div>
    );
}
